<template>
  <div class="poi-container" v-show="!sunstudyMode && currentViewPOIs.length">
    <div class="poi-box"
         v-for="(poi, index) in currentViewPOIs"
         :key="index"
         :class="{ active: forsedShownIndex === index || activeIndex === index }"
         @mouseover="showContent(index)"
         @mouseleave="hideContent(index)"
         :style="{
         top: `${getTopAlignInPx(poi.y)}px`,
         left: `${getLeftAlignInPx(poi.x)}px`,
       }"
    >
      <div class="poi-box-header"
           @click="showPoiContent(index)"
           :style="getStylesForHeader(index, poi)"
      >
        <div class="poi-box-header__icon">
          <img :src="poi.svg_url" :style="{transform: `rotate(${poi.rotation_overrider}deg)`}" />
        </div>
        <transition name="slide-fade">
          <div class="poi-box-header__title"
               :style="getStyleForTitle(poi)"
               v-if="forsedShownIndex === index || activeIndex === index">
            <div>{{ getTitle(poi) }}</div>
            <template v-if="poi.description">
              <img v-if="forsedShownIndex === index" :src="imageUrl('img/close-png.png')" />
              <img v-else :src="imageUrl('img/down-png.png')" />
            </template>

          </div>
        </transition>
      </div>

      <transition name="poi-body-animation">
        <div  class="poi-box-content"
              :style="{ left: getPoiFitToClientWidth(poi)? 0 : '-220px'}"
              v-if="forsedShownIndex === index"
              v-html="poi.description">
        </div>
      </transition>
    </div>
    <CarouselModal ref="carouselModal"
                   :gallery="lightboxVideo"
                   isMinimalistic
                   @overlay-opened="handleOverlayOpened"
                   @overlay-closed="handleOverlayClosed"  />
  </div>

</template>

<script>
import { imageUrl } from '@/utils/urls';
import { mapGetters } from "vuex";
import CarouselModal from "@/components/CarouselModal.vue";

export default {
  name: 'PointOfInterest',
  components: {CarouselModal},
  props: {
    width: {
      type: [ String, Number],
      required: true,
    },
    exterior: {
      type: Object,
    },
    height: {
      type: [ String, Number],
      required: true,
    }
  },
  data() {
    return {
      activeIndex: null,
      forsedShownIndex: null,
      lightboxVideo: [],
    }
  },
  computed: {
    ...mapGetters('sunstudy', [
      'sunstudyMode',
    ]),
    ...mapGetters('exteriors', ['poi_list', 'exteriors']),
    currentView() {
      return this.$route.params.exteriorId
    },
    currentViewPOIs() {
      const targetViewId = Number(this.currentView || this.exterior.exterior_id);
      return this.poi_list.filter(poi => {
        return poi.views.some(({id}) => id === targetViewId);
      });
    }
  },
  watch: {
    currentView() {
      this.forsedShownIndex = null;
    },
  },
  methods: {
    imageUrl,
    getTitle(poi) {
      if (!poi.title && poi.mode === 'view') {
        const view = this.exteriors.find(ext => ext.exterior_id === poi.link_to_view)
        return view.name
      } else {
        return poi.title
      }
    },
    showPoiContent(idx) {
      switch (this.currentViewPOIs[idx].mode) {
        case 'text': {
          if (this.currentViewPOIs[idx].description) {
            this.forsedShownIndex = idx === this.forsedShownIndex ? null : idx;
          } else {
            this.forsedShownIndex = null;
          }
          break;
        }
        case 'ext_link': {
          if (this.currentViewPOIs[idx].url) {
            window.open(this.currentViewPOIs[idx].url, this.currentViewPOIs[idx].extlink_target);
          } else {
            window.open('', '_blank');
          }
          break;
        }
        case 'view': {
          this.$router.push({ name: 'order.overview.exterior', params: { exteriorId: this.currentViewPOIs[idx].link_to_view } });
          break;
        }
        case 'lightbox': {
          this.lightboxVideo = [{ video: this.currentViewPOIs[idx].url }]
          this.openCarouselOnIdx(0, 'lightbox');
          this.hideContent();
          break;
        }
        case 'lightbox_resp': {
          this.lightboxVideo = [{ video: this.currentViewPOIs[idx].url }]
          this.openCarouselOnIdx(0, 'lightbox_resp');
          this.hideContent();
          break;
        }
      }

    },
    openCarouselOnIdx(index, mode = null) {
      this.$refs.carouselModal.openOnIdx(index, mode);
    },
    handleOverlayOpened() {
      const mainContent = document.querySelector('main.v-content');
      if (mainContent) {
        mainContent.classList.add('overlay-open');
      }
    },
    handleOverlayClosed() {
      const mainContent = document.querySelector('main.v-content');
      if (mainContent) {
        mainContent.classList.remove('overlay-open');
      }
    },
    getLeftAlignInPx(x) {
      return this.width * x - 23;
    },
    getTopAlignInPx(top) {
      return this.height * top - 20;
    },
    getStyleForTitle(poi) {
      let styles = {}
      if (!this.getPoiFitToClientWidth(poi)) {
        styles.left = '0';
        styles['flex-direction'] = 'row-reverse';
      }
      return styles;
    },
    getStylesForHeader(index, poi) {
      let openedStyles = {}

      if (this.forsedShownIndex === index || this.activeIndex === index) {
        openedStyles = {
          'border-radius': '5px',
          width: '260px',
          'z-index': 9999
        }
        if (!this.getPoiFitToClientWidth(poi)) {
          openedStyles['margin-left'] = '-220px';
          openedStyles['flex-direction'] = 'row-reverse';
          openedStyles['clip-path'] = 'inset(0 0 0 85%)';
          openedStyles['animation'] = ' clipPathAnimation 0.3s ease forwards';
        } else {
          openedStyles.transition = 'width 0.3s ease-in-out, border-radius 0.3s ease-in-out';
        }
      }
      if (this.forsedShownIndex === index ) {
        openedStyles['border-radius'] = '5px 5px 0 0';
      }

      return {
        ...openedStyles,
        cursor: this.setPointerCursor(poi)? 'pointer' : 'auto',
        'background-color': poi.color,
      }
    },
    showContent(index) {
      this.activeIndex = index;

      //toggle the highlight of the connected view/facade
      const poi = this.currentViewPOIs[index];
      const id = Number(poi && poi.link_to_view);
      if (!Number.isNaN(id)) {
        this.$store.commit('exteriors/SET_HOVERED_LINKED_VIEW_ID', id);
      } else {
        this.$store.commit('exteriors/SET_HOVERED_LINKED_VIEW_ID', null);
      }
    },
    getPoiFitToClientWidth(poi) {
      const viewportContainer = document.querySelector('.viewport-container');
      const clientWidth = document.body.clientWidth;
      const scrolledTo = viewportContainer.scrollLeft
      const neededPlace = this.getLeftAlignInPx(poi.x) + 260
      return neededPlace < clientWidth + scrolledTo
    },
    hideContent() {
      this.activeIndex = null;
      this.$store.commit('exteriors/SET_HOVERED_LINKED_VIEW_ID', null);
    },
    setPointerCursor(poi) {
      return poi.description || poi.mode === 'ext_link' ||  poi.mode === 'view' || poi.mode === 'lightbox' || poi.mode === 'lightbox_resp'
    }
  }
}
</script>

<style>
@keyframes clipPathAnimation {
  from {
    clip-path: inset(0 0 0 85%);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
</style>
<style scoped lang="scss">
.poi-container {
}
.poi-box {
  position: absolute;
  &.active{
    z-index: 3;
  }
  &-header {
    position: relative;
    display: flex;
    align-items: center;
    width: 40px;
    border-radius: 20px;
    @media (max-width: 1500px), (max-height: 920px) {
      width: 34px;
    }
    //transition: width 0.3s ease-in-out, border-radius 0.3s ease-in-out;
    &__icon {
      color: white;
      width: 40px;
      height: 40px;
      @media (max-width: 1500px), (max-height: 920px) {
        width: 34px;
        height: 34px;
      }
      & img {
        width: 40px;
        height: 40px;
        @media (max-width: 1500px), (max-height: 920px) {
          width: 34px;
          height: 34px;
        }
      }
    }
    &__title {
      position: absolute;
      line-height: 1;
      left: 30px;
      width: 230px;
      height: 36px;
      display: flex;
      color: white;
      align-items: center;
      justify-content: space-between;
      padding: 0 10px;
      & img {
        cursor: pointer;
        width: 16px;
        height: 16px;
      }
    }
  }
  &-content {
    position: absolute;
    width: 260px;
    padding: 10px;
    background-color: rgb(36,36,35);
    color: white;
    border-radius: 0 0 5px 5px;
    transform: perspective(200px) rotateX(0deg);
    transform-origin: 50% 0 0;
  }
}
.slide-fade-enter-active {
  animation-delay: 0.2s;
  visibility: hidden;
  transition: all .15s ease;
}

.poi-body-animation-enter-active {
  transition: transform 0.3s ease;
}
.poi-body-animation-leave-active {
  transition: transform 0.1s ease;
}
.poi-body-animation-enter {
  transform: perspective(200px) rotateX(-89.999deg);
}
.poi-body-animation-leave-to {
  transform: perspective(200px) rotateX(-89.999deg);
}
</style>
